/**
 * One approval need in words (19 Oct, UI review finding 3): its status in a
 * word for the badge, and who decided it and when for the line below. Names,
 * dates and the comment never go inside the badge.
 */

import { useDateFormatter } from "@/components/common";
import { useT } from "@/i18n";
import type { AccessApprovalNeed } from "@/lib/api";

/** One need's status in a word, for its badge: "Approved", "Rejected", "Waiting", … */
export function useNeedStatus() {
  const { t } = useT("tickets");
  return (need: AccessApprovalNeed): string => {
    switch (need.state) {
      case "approved":
      case "rejected":
      case "pre_approved":
      case "no_approver":
        return t(`access.approvals.badge.${need.state}`);
      default:
        return t("access.approvals.badge.waiting");
    }
  };
}

/** Who decided a need and when, or who it waits for; null when there is nothing to say. */
export function useNeedDetail() {
  const { t } = useT("tickets");
  const { formatDate } = useDateFormatter();
  return (need: AccessApprovalNeed): string | null => {
    const name = need.decided_by ?? "—";
    const date = need.decided_at ? formatDate(new Date(need.decided_at)) : "—";
    switch (need.state) {
      case "approved":
        return t("access.approvals.state.approved", { name, date });
      case "rejected":
        return t("access.approvals.state.rejected", { name, date });
      case "waiting":
        return need.approver ? t("access.approvals.state.waiting", { name: need.approver }) : null;
      default:
        return null;
    }
  };
}
